import React, { useEffect, useState, useRef, useCallback } from 'react';
import { supabaseAdmin } from '../config/supabase';
import toast from 'react-hot-toast';
import './AppSettings.css';

export default function AppSettings() {
  const [settings, setSettings] = useState({
    gradients: {
      tiles: { start: '#a00000', end: '#d40000' },
      buttons: { start: '#a00000', end: '#d40000' },
      topbar: { start: '#a00000', end: '#d40000' },
      sidemenu: { start: '#a00000', end: '#d40000' },
      info: { start: '#a00000', end: '#d40000' },
      newsSection: { start: '#0E2A47', end: '#0E2A47' },
    },
    showNewsCarousel: true,
    logo: { url: null, updated_at: null },
    headerBackground: { url: null, updated_at: null },
    bipLogo: { url: null, updated_at: null },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // ✅ Stan dla aktualnie wybranego pliku logo (używane tylko do setSelectedLogoFile)
  // eslint-disable-next-line no-unused-vars
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  // ✅ Ref do input file dla loga
  const logoFileInputRef = useRef(null);

  const loadSettings = useCallback(async () => {
    try {
      const { data, error } = await supabaseAdmin
        .from('app_settings')
        .select('*');

      if (error) throw error;

      const settingsObj = {};
      data.forEach(item => {
        settingsObj[item.setting_key] = item.setting_value;
      });

      // Ensure all gradient types exist (fallback for new gradient types)
      const loadedGradients = settingsObj.gradients || {};
      const defaultGradients = {
        tiles: { start: '#a00000', end: '#d40000' },
        buttons: { start: '#a00000', end: '#d40000' },
        topbar: { start: '#a00000', end: '#d40000' },
        sidemenu: { start: '#a00000', end: '#d40000' },
        info: { start: '#a00000', end: '#d40000' },
        newsSection: { start: '#0E2A47', end: '#0E2A47' },
      };
      const mergedGradients = {
        ...defaultGradients,
        ...loadedGradients,
        // Ensure newsSection exists (for existing databases without this field)
        newsSection: loadedGradients.newsSection || defaultGradients.newsSection,
      };

      setSettings(prevSettings => ({
        gradients: mergedGradients,
        showNewsCarousel: settingsObj.showNewsCarousel !== false,
        logo: settingsObj.logo || prevSettings.logo,
        headerBackground: settingsObj.headerBackground || prevSettings.headerBackground,
        bipLogo: settingsObj.bipLogo || prevSettings.bipLogo,
      }));
    } catch (error) {
      toast.error('Błąd podczas ładowania ustawień');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleGradientChange = (group, type, value) => {
    setSettings({
      ...settings,
      gradients: {
        ...settings.gradients,
        [group]: {
          ...settings.gradients[group],
          [type]: value,
        },
      },
    });
  };

  const handleImageUpload = async (type, file) => {
    // ✅ WALIDACJA: Sprawdź czy plik został wybrany
    if (!file) {
      toast.error('Nie wybrano pliku');
      return;
    }

    try {
      // Dla loga używamy stałej ścieżki logo/logo.png
      // Dla headerBackground używamy stałej ścieżki header-background/header-background.png
      // Dla bipLogo używamy stałej ścieżki bip-logo/bip-logo.png
      // Dla innych typów (background) używamy dynamicznej ścieżki
      let filePath;
      if (type === 'logo') {
        filePath = 'logo/logo.png';
      } else if (type === 'headerBackground') {
        filePath = 'header-background/header-background.png';
      } else if (type === 'bipLogo') {
        filePath = 'bip-logo/bip-logo.png';
      } else {
        const fileName = `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${file.name.split('.').pop()}`;
        filePath = `app-assets/${fileName}`;
      }

      const { error: uploadError } = await supabaseAdmin.storage
        .from('app-assets')
        .upload(filePath, file, {
          contentType: file.type,
          upsert: true, // Nadpisuje istniejący plik dla loga
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabaseAdmin.storage
        .from('app-assets')
        .getPublicUrl(filePath);

      const updatedSettings = {
        ...settings,
        [type]: {
          url: publicUrl,
          updated_at: new Date().toISOString(),
        },
      };

      setSettings(updatedSettings);

      // Dla loga, bipLogo i headerBackground automatycznie zapisz do bazy po uploadzie
      if (type === 'logo') {
        try {
          const { error: logoError } = await supabaseAdmin
            .from('app_settings')
            .upsert({
              setting_key: 'logo',
              setting_value: updatedSettings.logo,
              updated_at: new Date().toISOString(),
            }, {
              onConflict: 'setting_key',
            });

          if (logoError) {
            console.error('Error saving logo to database:', logoError);
            toast.error('Logo przesłane, ale wystąpił błąd podczas zapisywania do bazy');
          } else {
            toast.success('Logo przesłane i zapisane');
          }
        } catch (dbError) {
          console.error('Error saving logo to database:', dbError);
          toast.error('Logo przesłane, ale wystąpił błąd podczas zapisywania do bazy');
        }
        
        // ✅ WYCZYŚĆ STAN I INPUT PO UDANYM UPLOADZIE
        setSelectedLogoFile(null);
        if (logoFileInputRef.current) {
          logoFileInputRef.current.value = '';
        }
      } else if (type === 'bipLogo') {
        try {
          const { error: bipLogoError } = await supabaseAdmin
            .from('app_settings')
            .upsert({
              setting_key: 'bipLogo',
              setting_value: updatedSettings.bipLogo,
              updated_at: new Date().toISOString(),
            }, {
              onConflict: 'setting_key',
            });

          if (bipLogoError) {
            console.error('Error saving bipLogo to database:', bipLogoError);
            toast.error('Logo BIP przesłane, ale wystąpił błąd podczas zapisywania do bazy');
          } else {
            toast.success('Logo BIP przesłane i zapisane');
          }
        } catch (dbError) {
          console.error('Error saving bipLogo to database:', dbError);
          toast.error('Logo BIP przesłane, ale wystąpił błąd podczas zapisywania do bazy');
        }
      } else if (type === 'headerBackground') {
        try {
          const { error: headerBgError } = await supabaseAdmin
            .from('app_settings')
            .upsert({
              setting_key: 'headerBackground',
              setting_value: updatedSettings.headerBackground,
              updated_at: new Date().toISOString(),
            }, {
              onConflict: 'setting_key',
            });

          if (headerBgError) {
            console.error('Error saving header background to database:', headerBgError);
            toast.error('Zdjęcie tła przesłane, ale wystąpił błąd podczas zapisywania do bazy');
          } else {
            toast.success('Zdjęcie tła headera przesłane i zapisane');
          }
        } catch (dbError) {
          console.error('Error saving header background to database:', dbError);
          toast.error('Zdjęcie tła przesłane, ale wystąpił błąd podczas zapisywania do bazy');
        }
      } else {
        toast.success('Zdjęcie przesłane');
      }
    } catch (error) {
      toast.error('Błąd podczas przesyłania zdjęcia');
      console.error(error);
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Czy na pewno chcesz usunąć logo? Logo zniknie z aplikacji mobilnej.')) {
      return;
    }

    try {
      // Usuń plik z Storage
      const { error: deleteError } = await supabaseAdmin.storage
        .from('app-assets')
        .remove(['logo/logo.png']);

      if (deleteError && deleteError.message !== 'The resource was not found') {
        console.error('Error deleting logo from storage:', deleteError);
        // Kontynuuj nawet jeśli plik nie istnieje
      }

      // ✅ WYCZYŚĆ WSZYSTKIE STANY ZWIĄZANE Z LOGO
      // 1. Wyczyść logo w state
      const updatedSettings = {
        ...settings,
        logo: {
          url: null,
          updated_at: new Date().toISOString(),
        },
      };

      setSettings(updatedSettings);

      // 2. Wyczyść wybrany plik
      setSelectedLogoFile(null);

      // 3. Zresetuj input file
      if (logoFileInputRef.current) {
        logoFileInputRef.current.value = '';
      }

      // Wyczyść logo w bazie danych
      const { error: logoError } = await supabaseAdmin
        .from('app_settings')
        .upsert({
          setting_key: 'logo',
          setting_value: updatedSettings.logo,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'setting_key',
        });

      if (logoError) {
        console.error('Error deleting logo from database:', logoError);
        toast.error('Błąd podczas usuwania logo z bazy danych');
      } else {
        toast.success('Logo usunięte');
      }
    } catch (error) {
      toast.error('Błąd podczas usuwania logo');
      console.error(error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Zapisz gradients
      const { error: gradientsError } = await supabaseAdmin
        .from('app_settings')
        .upsert({
          setting_key: 'gradients',
          setting_value: settings.gradients,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'setting_key',
        });

      if (gradientsError) throw gradientsError;

      // Zapisz showNewsCarousel
      const { error: showNewsCarouselError } = await supabaseAdmin
        .from('app_settings')
        .upsert({
          setting_key: 'showNewsCarousel',
          setting_value: settings.showNewsCarousel,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'setting_key',
        });

      if (showNewsCarouselError) throw showNewsCarouselError;

      // Zapisz logo
      const { error: logoError } = await supabaseAdmin
        .from('app_settings')
        .upsert({
          setting_key: 'logo',
          setting_value: settings.logo,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'setting_key',
        });

      if (logoError) throw logoError;

      // Zapisz headerBackground
      const { error: headerBgError } = await supabaseAdmin
        .from('app_settings')
        .upsert({
          setting_key: 'headerBackground',
          setting_value: settings.headerBackground,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'setting_key',
        });

      if (headerBgError) throw headerBgError;

      // Zapisz bipLogo
      const { error: bipLogoError } = await supabaseAdmin
        .from('app_settings')
        .upsert({
          setting_key: 'bipLogo',
          setting_value: settings.bipLogo,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'setting_key',
        });

      if (bipLogoError) throw bipLogoError;

      if (settings.logo.url || settings.headerBackground?.url || settings.bipLogo?.url) {
        const messages = [];
        if (settings.logo.url) messages.push('Logo zapisane');
        if (settings.headerBackground?.url) messages.push('Zdjęcie tła headera zapisane');
        if (settings.bipLogo?.url) messages.push('Logo BIP zapisane');
        toast.success(`Ustawienia zapisane. ${messages.join('. ')}. Zmiany będą widoczne w aplikacji w ciągu 20-30 sekund.`);
      } else {
        toast.success('Ustawienia zapisane. Zmiany będą widoczne w aplikacji w ciągu 20-30 sekund.');
      }
    } catch (error) {
      toast.error('Błąd podczas zapisywania');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Ładowanie...</div>;
  }

  return (
    <div className="app-settings">
      <h2>Zarządzanie wyglądem aplikacji</h2>

      <div className="card">
        <h3>Elementy aplikacji</h3>
        <p className="info-text" style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
          Widoczność karuzeli aktualności oraz kolory przycisków, górnego paska i menu bocznego. Dwa takie same kolory = kolor jednolity (bez gradientu).
        </p>

        {/* Karuzela aktualności na start screen */}
        <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid #E5E5EA' }}>
          <h4 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '600' }}>Karuzela aktualności na ekranie startowym</h4>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={settings.showNewsCarousel}
              onChange={(e) => setSettings({ ...settings, showNewsCarousel: e.target.checked })}
              style={{ width: '20px', height: '20px', cursor: 'pointer' }}
            />
            <span>Pokaż karuzelę aktualności na ekranie startowym</span>
          </label>
        </div>

        {/* Buttons gradient */}
        <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid #E5E5EA' }}>
          <h4 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '600' }}>Przyciski (wszystkie w aplikacji mobilnej)</h4>
          <div className="settings-grid">
            <div className="form-group">
              <label className="form-label">Kolor początkowy</label>
              <div className="color-input-group">
                <input
                  type="color"
                  value={settings.gradients.buttons.start}
                  onChange={(e) => handleGradientChange('buttons', 'start', e.target.value)}
                  className="color-picker"
                />
                <input
                  type="text"
                  className="form-input"
                  value={settings.gradients.buttons.start}
                  onChange={(e) => handleGradientChange('buttons', 'start', e.target.value)}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Kolor końcowy</label>
              <div className="color-input-group">
                <input
                  type="color"
                  value={settings.gradients.buttons.end}
                  onChange={(e) => handleGradientChange('buttons', 'end', e.target.value)}
                  className="color-picker"
                />
                <input
                  type="text"
                  className="form-input"
                  value={settings.gradients.buttons.end}
                  onChange={(e) => handleGradientChange('buttons', 'end', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* TopBar gradient */}
        <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid #E5E5EA' }}>
          <h4 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '600' }}>Górny pasek (Secondary TopBar)</h4>
          <div className="settings-grid">
            <div className="form-group">
              <label className="form-label">Kolor początkowy</label>
              <div className="color-input-group">
                <input
                  type="color"
                  value={settings.gradients.topbar.start}
                  onChange={(e) => handleGradientChange('topbar', 'start', e.target.value)}
                  className="color-picker"
                />
                <input
                  type="text"
                  className="form-input"
                  value={settings.gradients.topbar.start}
                  onChange={(e) => handleGradientChange('topbar', 'start', e.target.value)}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Kolor końcowy</label>
              <div className="color-input-group">
                <input
                  type="color"
                  value={settings.gradients.topbar.end}
                  onChange={(e) => handleGradientChange('topbar', 'end', e.target.value)}
                  className="color-picker"
                />
                <input
                  type="text"
                  className="form-input"
                  value={settings.gradients.topbar.end}
                  onChange={(e) => handleGradientChange('topbar', 'end', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* SideMenu gradient */}
        <div style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: '1px solid #E5E5EA' }}>
          <h4 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '600' }}>Menu boczne (SideMenu)</h4>
          <div className="settings-grid">
            <div className="form-group">
              <label className="form-label">Kolor początkowy</label>
              <div className="color-input-group">
                <input
                  type="color"
                  value={settings.gradients.sidemenu.start}
                  onChange={(e) => handleGradientChange('sidemenu', 'start', e.target.value)}
                  className="color-picker"
                />
                <input
                  type="text"
                  className="form-input"
                  value={settings.gradients.sidemenu.start}
                  onChange={(e) => handleGradientChange('sidemenu', 'start', e.target.value)}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Kolor końcowy</label>
              <div className="color-input-group">
                <input
                  type="color"
                  value={settings.gradients.sidemenu.end}
                  onChange={(e) => handleGradientChange('sidemenu', 'end', e.target.value)}
                  className="color-picker"
                />
                <input
                  type="text"
                  className="form-input"
                  value={settings.gradients.sidemenu.end}
                  onChange={(e) => handleGradientChange('sidemenu', 'end', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <h3>Logo aplikacji w O aplikacji</h3>
        <div className="form-group">
          <label className="form-label">Logo aplikacji</label>
          <p className="info-text" style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
            Zalecany rozmiar: 64×64 px. Logo będzie wyświetlane w headerze aplikacji mobilnej.
          </p>
          {settings.logo.url && (
            <div className="image-preview" style={{ marginBottom: '12px' }}>
              <img src={settings.logo.url} alt="Logo" style={{ maxWidth: '128px', maxHeight: '128px' }} />
            </div>
          )}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
            <label className="btn btn-primary" style={{ cursor: 'pointer', display: 'inline-block', padding: '10px 20px' }}>
              Wgraj nowe logo
              <input
                ref={logoFileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    // ✅ ZAPISZ PLIK DO STANU (nie uploaduj od razu)
                    setSelectedLogoFile(file);
                    
                    // Walidacja rozmiaru (opcjonalnie)
                    const img = new Image();
                    img.onload = () => {
                      if (img.width !== 64 || img.height !== 64) {
                        if (!window.confirm(`Logo ma rozmiar ${img.width}×${img.height} px. Zalecany rozmiar to 64×64 px. Kontynuować?`)) {
                          // Anulowano - wyczyść stan i input
                          setSelectedLogoFile(null);
                          e.target.value = '';
                          return;
                        }
                      }
                      // ✅ UPLOAD TYLKO PO WALIDACJI I ZAPISANIU DO STANU
                      handleImageUpload('logo', file);
                    };
                    img.onerror = () => {
                      toast.error('Błąd podczas ładowania obrazu');
                      setSelectedLogoFile(null);
                      e.target.value = '';
                    };
                    img.src = URL.createObjectURL(file);
                  } else {
                    // Brak pliku - wyczyść stan
                    setSelectedLogoFile(null);
                  }
                }}
                style={{ display: 'none' }}
              />
            </label>
            {settings.logo.url && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteLogo}
              >
                Usuń logo
              </button>
            )}
          </div>
          {!settings.logo.url && (
            <p style={{ fontSize: '14px', color: '#999', fontStyle: 'italic' }}>
              Brak logo. Wgraj logo, aby wyświetlało się w aplikacji mobilnej.
            </p>
          )}
        </div>
      </div>

      <div className="card">
        <h3>Zdjęcie tła headera</h3>
        <div className="form-group">
          <label className="form-label">Zdjęcie tła headera aplikacji</label>
          <p className="info-text" style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
            Zalecana wysokość: 220px. Zdjęcie będzie wyświetlane w headerze aplikacji mobilnej. 
            Zdjęcie NIE będzie kadrowane - wyświetli się w całości z zachowaniem proporcji.
          </p>
          {settings.headerBackground?.url && (
            <div className="image-preview" style={{ marginBottom: '12px' }}>
              <img src={settings.headerBackground.url} alt="Header Background" style={{ maxWidth: '400px', maxHeight: '220px', border: '1px solid #ddd', borderRadius: '8px' }} />
            </div>
          )}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
            <label className="btn btn-primary" style={{ cursor: 'pointer', display: 'inline-block', padding: '10px 20px' }}>
              Wgraj zdjęcie tła
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) handleImageUpload('headerBackground', file);
                }}
                style={{ display: 'none' }}
              />
            </label>
            {settings.headerBackground?.url && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={async () => {
                  if (!window.confirm('Czy na pewno chcesz usunąć zdjęcie tła headera?')) {
                    return;
                  }

                  try {
                    const { error: deleteError } = await supabaseAdmin.storage
                      .from('app-assets')
                      .remove(['header-background/header-background.png']);

                    if (deleteError && deleteError.message !== 'The resource was not found') {
                      console.error('Error deleting header background from storage:', deleteError);
                    }

                    const updatedSettings = {
                      ...settings,
                      headerBackground: {
                        url: null,
                        updated_at: new Date().toISOString(),
                      },
                    };

                    setSettings(updatedSettings);

                    const { error: bgError } = await supabaseAdmin
                      .from('app_settings')
                      .upsert({
                        setting_key: 'headerBackground',
                        setting_value: updatedSettings.headerBackground,
                        updated_at: new Date().toISOString(),
                      }, {
                        onConflict: 'setting_key',
                      });

                    if (bgError) {
                      console.error('Error deleting header background from database:', bgError);
                      toast.error('Błąd podczas usuwania zdjęcia tła z bazy danych');
                    } else {
                      toast.success('Zdjęcie tła usunięte');
                    }
                  } catch (error) {
                    toast.error('Błąd podczas usuwania zdjęcia tła');
                  }
                }}
              >
                Usuń zdjęcie
              </button>
            )}
          </div>
          {!settings.headerBackground?.url && (
            <p style={{ fontSize: '14px', color: '#999', fontStyle: 'italic' }}>
              Brak zdjęcia tła. Wgraj zdjęcie, aby wyświetlało się w headerze aplikacji mobilnej.
            </p>
          )}
        </div>
      </div>

      <div className="card">
        <h3>Logo kafelka BIP i sidemenu BIP</h3>
        <div className="form-group">
          <label className="form-label">Logo Biuletynu Informacji Publicznej</label>
          <p className="info-text" style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
            Logo będzie wyświetlane w sekcji kontaktów aplikacji mobilnej.
          </p>
          {settings.bipLogo?.url && (
            <div className="image-preview" style={{ marginBottom: '12px' }}>
              <img src={settings.bipLogo.url} alt="BIP Logo" style={{ maxWidth: '200px', maxHeight: '200px' }} />
            </div>
          )}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
            <label className="btn btn-primary" style={{ cursor: 'pointer', display: 'inline-block', padding: '10px 20px' }}>
              Wgraj logo BIP
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) handleImageUpload('bipLogo', file);
                }}
                style={{ display: 'none' }}
              />
            </label>
            {settings.bipLogo?.url && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={async () => {
                  if (!window.confirm('Czy na pewno chcesz usunąć logo BIP?')) {
                    return;
                  }

                  try {
                    const { error: deleteError } = await supabaseAdmin.storage
                      .from('app-assets')
                      .remove(['bip-logo/bip-logo.png']);

                    if (deleteError && deleteError.message !== 'The resource was not found') {
                      console.error('Error deleting bipLogo from storage:', deleteError);
                    }

                    const updatedSettings = {
                      ...settings,
                      bipLogo: {
                        url: null,
                        updated_at: new Date().toISOString(),
                      },
                    };

                    setSettings(updatedSettings);

                    const { error: bipLogoError } = await supabaseAdmin
                      .from('app_settings')
                      .upsert({
                        setting_key: 'bipLogo',
                        setting_value: updatedSettings.bipLogo,
                        updated_at: new Date().toISOString(),
                      }, {
                        onConflict: 'setting_key',
                      });

                    if (bipLogoError) {
                      console.error('Error deleting bipLogo from database:', bipLogoError);
                      toast.error('Błąd podczas usuwania logo BIP z bazy danych');
                    } else {
                      toast.success('Logo BIP usunięte');
                    }
                  } catch (error) {
                    toast.error('Błąd podczas usuwania logo BIP');
                  }
                }}
              >
                Usuń logo
              </button>
            )}
          </div>
          {!settings.bipLogo?.url && (
            <p style={{ fontSize: '14px', color: '#999', fontStyle: 'italic' }}>
              Brak logo BIP. Wgraj logo, aby wyświetlało się w sekcji kontaktów aplikacji mobilnej.
            </p>
          )}
        </div>
      </div>

      <div className="card">
        <button
          className="btn btn-primary btn-large"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Zapisywanie...' : 'Zapisz zmiany'}
        </button>
        <p className="info-text">
          Zmiany będą automatycznie odświeżane w aplikacji mobilnej w ciągu 20-30 sekund.
        </p>
      </div>
    </div>
  );
}

